import React, { useState, useEffect, useRef, useCallback } from "react"
import PropTypes from "prop-types"
import { useDropzone } from "react-dropzone"
import axios from "axios"

import UploadingProgress from "./UploadProgress"
import * as Styled from "./Uploader.styled"
import Loader from "../loader/Loader"
import Modal from "../modal/Modal"
import useMediaQuery from "../../hooks/useMediaQuery"
import { refreshJwtToken } from "../../helpers"
import CloudSVG from "../../images/cloud-upload.svg"
import ApplyGrantSVG from "../../images/apply-grant.svg"
import DeleteSVG from "../../images/delete.svg"
import ReplaceSVG from "../../images/replace.svg"

const Uploader = ({
  action,
  isPrivateRequest = false,
  body = {},
  headers = {},
  dropzoneProps = {},
  onUploadSuccess,
  onUploadError,
  onRejectedFiles,
  beforeUpload,
  onCancel = () => {},
  onDelete = () => {},
  onReplace = () => {},
  isVerified = true,
  hasError = false,
  content,
}) => {
  const isTablet = useMediaQuery("(min-width: 768px)")

  const [isUploading, setIsUploading] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [fileUploadProgress, setFileUploadProgress] = useState({})

  const abortController = useRef({})
  const isRequestCancelled = useRef(false)

  // abort all pending requests on unmount
  useEffect(() => {
    return () => {
      Object.values(abortController.current)?.forEach((controller) => controller.abort())
    }
  }, [])

  useEffect(() => {
    if (typeof hasError !== "undefined" && !!hasError) {
      setIsCompleted(false)
      setIsUploading(false)
      setFileUploadProgress({})
    }
  }, [hasError])

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length) {
        onRejectedFiles?.(rejectedFiles)
        return
      }

      if (!acceptedFiles.length) return

      const canContinueUpload = beforeUpload ? beforeUpload(acceptedFiles[0], acceptedFiles) : true

      if (!canContinueUpload) return

      try {
        isRequestCancelled.current = false
        setIsUploading(true)

        await refreshJwtToken()

        const uploadPromises = acceptedFiles.map((file) => {
          const formData = new FormData()
          formData.append("file", file)

          Object.entries(body).forEach(([key, value]) => {
            formData.append(key, value);
          });
  
          abortController.current[file.name] = new AbortController()
  
          return axios.post(action, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
              ...(isPrivateRequest && { Authorization: `Bearer ${localStorage.getItem("JWTToken")}` }),
              ...headers,
            },
            onUploadProgress: (progressEvent) => {
              const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100)
              setFileUploadProgress((prevFileUploadProgress) => ({
                ...prevFileUploadProgress,
                [file.name]: progress,
              }))
            },
            signal: abortController.current[file.name].signal,
          })
        })

        const results = await Promise.allSettled(uploadPromises)

        if (!isRequestCancelled.current) {
          const failed = results.some(({ status }) => status !== "fulfilled")
          if (failed) {
            {
              /* TODO: Get from content */
            }
            const isInvalidFile = results?.[0]?.reason?.response?.data?.Detail === "Invalid file extension."
            const errorType = isInvalidFile ? "invalid-file" : "server"
            throw new Error(errorType)
          }
          onUploadSuccess?.(
            results.map((result) => result.value?.data),
            acceptedFiles[0]?.name
          )
          onUploadError?.({
            type: "",
            message: "",
          })
          setIsCompleted(true)
        }
      } catch (error) {
        if (!isRequestCancelled.current) {
          onUploadError?.({
            type: error.message,
            message: "Invalid file extension.", // TODO: Get from content
          })
        }
        setIsCompleted(false)
      } finally {
        setIsUploading(false)
        setFileUploadProgress({})
        abortController.current = {}
      }
    },
    [action, isPrivateRequest, body, headers, onUploadSuccess, onUploadError, onRejectedFiles, beforeUpload]
  )

  const { acceptedFiles, getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: isUploading,
    ...dropzoneProps,
  })

  const cancelUpload = () => {
    Object.values(abortController.current)?.forEach((controller) => controller.abort())
    isRequestCancelled.current = true
    setIsCompleted(false)
    setIsUploading(false)
    setFileUploadProgress({})
    abortController.current = {}
    onCancel()
  }

  const uploadProgress =
    Math.floor(Object.values(fileUploadProgress).reduce((acc, progress) => acc + progress, 0) / acceptedFiles.length) ||
    0 // calculate average progress

  const isReadyForUpload = !isUploading && !isCompleted

  return (
    <>
      <Styled.UploaderContainer isReadyForUpload={isReadyForUpload}>
        {isTablet && isUploading && (
          <UploadingProgress
            acceptedFiles={acceptedFiles}
            uploadProgress={uploadProgress}
            cancelUpload={cancelUpload}
            content={content}
          />
        )}
        {isReadyForUpload && (
          <UploadStartReady
            rootProps={{
              ...getRootProps({
                className: `uploader ${isDragActive && isReadyForUpload ? "active" : ""}`,
              }),
            }}
            inputProps={{ ...getInputProps() }}
            isDragActive={isDragActive}
            content={content}
          />
        )}
        {isCompleted && !isUploading && (
          <UploadCompleted
            fileName={acceptedFiles[0]?.name}
            onDelete={() => {
              setIsCompleted(false)
              onDelete()
            }}
            onReplace={() => {
              setIsCompleted(false)
              onReplace()
            }}
            isVerified={isVerified}
            hasError={hasError}
            content={content}
          />
        )}
      </Styled.UploaderContainer>
      {!isTablet && isUploading && (
        <Modal isOpen={isUploading} onClose={cancelUpload} fullScreenModal>
          <UploadingProgress
            acceptedFiles={acceptedFiles}
            uploadProgress={uploadProgress}
            cancelUpload={cancelUpload}
            content={content}
          />
        </Modal>
      )}
    </>
  )
}

function UploadCompleted({ fileName, onDelete, onReplace, isVerified, hasError }) {
  return (
    <Styled.CenteredContainer>
      {!isVerified && !hasError && <VerificationLoader />}
      <Styled.UploadCompletedContainer>
        <div>
          <img src={ApplyGrantSVG} />
          <div>
            {/* TODO: Get from content */}
            <p>Upload Completed</p>
            <p>{fileName}</p>
          </div>
        </div>
        <div>
          <button type="button" onClick={onDelete}>
            <img src={DeleteSVG} />
            {/* TODO: Get from content */}
            Delete
          </button>
          <button type="button" onClick={onReplace}>
            <img src={ReplaceSVG} />
            {/* TODO: Get from content */}
            Replace
          </button>
        </div>
      </Styled.UploadCompletedContainer>
    </Styled.CenteredContainer>
  )
}

function UploadStartReady({ inputProps, isDragActive, content, rootProps }) {
  return (
    <Styled.Uploader {...rootProps}>
      <input {...inputProps} />
      {isDragActive ? (
        <p>{content.dropFiles}</p>
      ) : (
        <>
          <Styled.CloudIconContainer>
            <Styled.CloudImage src={CloudSVG} alt={content.dragAndDrop} />
            <p>{content.dragAndDrop}</p>
            {content.fileSize && <p className="file-type">{content.fileSize}</p>}
            <p className="operator">{content.or}</p>
          </Styled.CloudIconContainer>
          <p className="mobile-only">{content.dragAndDrop}</p>
          <p className="browse-file">{content.browseFile}</p>
          {content.description && <p className="measurements">{content.description}</p>}
        </>
      )}
    </Styled.Uploader>
  )
}

function VerificationLoader() {
  return (
    <div
      style={{
        position: "absolute",
        width: "100%",
        height: "100%",
      }}
    >
      <Loader local />
      <p
        style={{
          position: "absolute",
          fontSize: "1.2rem",
          fontWeight: 700,
          lineHeight: "1.6rem",
          width: "100%",
          top: "65%",
        }}
      >
        {/* TODO: Get from content */}
        Verifying your file...
      </p>
    </div>
  )
}

Uploader.propTypes = {
  action: PropTypes.string,
  isPrivateRequest: PropTypes.bool,
  body: PropTypes.object,
  headers: PropTypes.object,
  dropzoneProps: PropTypes.object.isRequired,
  onUploadSuccess: PropTypes.func,
  onUploadError: PropTypes.func,
  onRejectedFiles: PropTypes.func,
  beforeUpload: PropTypes.func,
  onCancel: PropTypes.func,
  onDelete: PropTypes.func,
  onReplace: PropTypes.func,
  isVerified: PropTypes.bool,
  hasError: PropTypes.bool,
  content: PropTypes.object.isRequired,
}

export default Uploader
